import { useField, useStore } from '@tanstack/react-form'
import { LakehouseConnectionTypeEnum } from '@vendia/management-api-types'
import { useNavigate } from 'react-router'
import { ScrollableStepContent } from 'src/components/flows/scrollable-step-content'
import StepButtons from 'src/components/flows/step-buttons'
import { StepContentHeader } from 'src/components/flows/step-header'
import { StepWrapper } from 'src/components/flows/step-wrapper'
import { StepComponent } from 'src/components/flows/types'
import { SourceButton } from 'src/components/lakehouse/source-button'
import { isRequiredValidator } from 'src/utils/form/validation'
import { LakehouseFlowStepValues } from 'src/utils/lakehouse/types'
import { lakehouseFlowFieldValidator } from 'src/utils/lakehouse/validators'

import { StepId } from '../config'

export const StepChooseSource: StepComponent<LakehouseFlowStepValues> = ({ context }) => {
  const navigate = useNavigate()
  const form = context.form

  const { existingVendiaTables } = useStore(form.store, (state) => state.values)
  const hasVendiaTableNames = !!(existingVendiaTables && existingVendiaTables.length)

  useField({
    form,
    name: 'source.connection',
    validators: lakehouseFlowFieldValidator({
      stepId: StepId.ChooseSource,
      validator: ({ value, fieldApi }) => {
        // Don't require source.connection if we have a connectionId set (existing connection flow)
        const formValues = fieldApi.form.state.values
        if (formValues.connectionId) {
          return undefined
        }
        return isRequiredValidator()({ value })
      },
    }),
  })

  return (
    <StepWrapper>
      <StepContentHeader
        centered
        large
        title='Choose your data source'
        description={
          hasVendiaTableNames
            ? 'Connect an external data warehouse or use your existing Vendia table(s)'
            : 'Connect your existing data warehouse'
        }
      />
      <ScrollableStepContent inset autoHeight>
        <div className='flex max-w-6xl flex-wrap justify-center p-2'>
          <SourceButton
            label='BigQuery'
            description='Connect to your data in BigQuery'
            imageSrc='/images/vendor/bigquery-primary.png'
            connection={LakehouseConnectionTypeEnum.Bigquery}
            context={context}
            to={StepId.ConnectBigQuery}
            linkText='Connect'
          />
          <SourceButton
            label='Snowflake'
            description='Connect to your data in Snowflake'
            imageSrc='/images/vendor/snowflake-primary.png'
            context={context}
            connection={LakehouseConnectionTypeEnum.Snowflake}
            to={StepId.ConnectSnowflake}
            linkText='Connect'
          />
          <SourceButton
            label='Cloudera'
            description='Connect to your data in Cloudera'
            imageSrc='/images/vendor/cloudera-primary.png'
            connection={LakehouseConnectionTypeEnum.Cloudera}
            context={context}
            to={StepId.ConnectCloudera}
            linkText='Connect'
          />
          <SourceButton
            label='PostgreSQL'
            description='Connect to your data in PostgreSQL'
            imageSrc='/images/vendor/postgres-primary.png'
            connection={LakehouseConnectionTypeEnum.Postgres}
            context={context}
            to={StepId.ConnectPostgres}
            linkText='Connect'
          />
          <SourceButton
            label='MySQL'
            description='Connect to your data in MySQL'
            imageSrc='/images/vendor/mysql-primary.png'
            connection={LakehouseConnectionTypeEnum.Mysql}
            context={context}
            to={StepId.ConnectMySql}
            linkText='Connect'
          />
          <SourceButton
            label='Amazon S3'
            description='Connect to your data in Amazon S3'
            imageSrc='/images/vendor/s3-primary.png'
            connection={LakehouseConnectionTypeEnum.S3}
            context={context}
            to={StepId.ConnectS3}
            linkText='Connect'
          />
          <SourceButton
            label='Databricks'
            description='Connect to your data in Databricks'
            imageSrc='/images/vendor/databricks-primary.png'
            connection={LakehouseConnectionTypeEnum.Databricks}
            context={context}
            to={StepId.ConnectDatabricks}
            linkText='Connect'
          />
          <SourceButton
            label='Existing Connection'
            description='Use the existing connection already configured in this project'
            imageSrc='/images/rocket.svg'
            context={context}
            to={StepId.SelectConnection}
          />
          {hasVendiaTableNames && (
            <SourceButton
              label='Vendia Tables'
              description='Use your existing Vendia Table(s) as a data source'
              imageSrc='/images/vendor/vendia-primary.png'
              connection={LakehouseConnectionTypeEnum.Vendia}
              context={context}
              to={StepId.SelectVendiaTables}
              linkText='Configure'
            />
          )}
          <SourceButton
            label='Upload'
            description='Add a CSV file from your computer'
            imageSrc='/images/vendor/upload-primary.png'
            connection={LakehouseConnectionTypeEnum.Upload}
            context={context}
            to={StepId.UploadFile}
            linkText='Upload'
          />
        </div>
      </ScrollableStepContent>
      <StepButtons
        context={context}
        hasNext={false}
        hasPrevious={false}
        hasCancel={true}
        onCancel={() => navigate('../', { relative: 'path' })}
      />
    </StepWrapper>
  )
}
